/**
 * useFluidGrid — orchestrates the visible grid + section resize handle +
 * snap guides for the Fluid Engine (Iter 145 Path A, Session 1).
 *
 * Wiring:
 *   • Adds a mouseenter/mouseleave listener on every section in the
 *     canvas. On enter → toggles `data-wb-grid-active="1"` on the
 *     section so its grid overlay fades in.
 *   • Attaches a `<button class="wb-section-resize-handle">` to each
 *     section at mount via a MutationObserver-friendly effect. Drag the
 *     handle to grow / shrink the section's `min-height`, persisted to
 *     `block.content._fluid_section_height`.
 *   • Provides global `setSnapGuides(guidesArray)` + `renderSnapGuides()`
 *     so `useFluidTextDrag` can draw blue alignment lines while dragging.
 */
import { useEffect, useRef } from 'react';
import { ensureGridOverlayStyles } from './GridOverlay';


export function useFluidGrid({ canvasRef, blocks, onUpdateBlockContent, enabled = true }) {
  const blocksRef = useRef(blocks);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  // 1) Inject the grid CSS once.
  useEffect(() => { ensureGridOverlayStyles(); }, []);

  // 2) Iter 155 — Grid is HIDDEN by default. It only appears while a
  //    fluid drag is in progress (useFluidTextDrag.beginDragFor sets
  //    `data-wb-grid-active` on the affected section + body flag) or
  //    while the section is being resized. The previous mouseenter/leave
  //    hover affordance was removed because it made the grid feel
  //    permanently visible and distracting (user feedback Iter 155).
  // (Intentionally empty — kept as a documented anchor.)

  // 3) Iter 156 — Per-section grid overlay + resize handle are now
  //    rendered via a React PORTAL (<FluidOverlayLayer>) at document.body
  //    level instead of being imperatively injected into React-managed
  //    section parents. This eliminates the "Failed to execute removeChild
  //    on Node" reconciliation crashes the previous implementation caused
  //    when React re-rendered a section.
  //
  //    The only side-effect this hook still applies to each section is
  //    persisting its `_fluid_section_height` from block.content.
  //    Persisted `_fluid_section_height` is now applied declaratively by
  //    FullBlock so Edit and Preview share it (and React-owned min-heights
  //    from section_height are never wiped imperatively).

  // ── Section-resize drag implementation ────────────────────────────
  // Iter 156 — Wrapped so the portal-rendered handle can call it with
  // just (event, blockId); the function looks up the section element
  // from the DOM.
  const startResize = (e, sectionOrBlockId, maybeBlockId) => {
    let section, blockId;
    if (typeof sectionOrBlockId === 'string') {
      blockId = sectionOrBlockId;
      section = document.querySelector(`[data-block-id="${blockId}"]`);
    } else {
      section = sectionOrBlockId;
      blockId = maybeBlockId;
    }
    if (!section) return;
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startH = section.getBoundingClientRect().height;
    section.setAttribute('data-wb-grid-active', '1');
    document.body.setAttribute('data-wb-grid-dragging', '1');
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';

    // Live HUD showing height + rows
    const hud = document.createElement('div');
    hud.className = 'wb-resize-hud';
    hud.style.cssText = `
      position: fixed; z-index: 9999; padding: 8px 14px;
      background: #0E0E23; color: #fff; border-radius: 999px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.5px;
      box-shadow: 0 8px 24px rgba(14,14,35,0.45);
      pointer-events: none;
    `;
    hud.textContent = `${Math.round(startH)}px · ${Math.round(startH / 60)} rows`;
    document.body.appendChild(hud);
    const moveHud = (ev) => {
      hud.style.left = `${ev.clientX + 16}px`;
      hud.style.top  = `${ev.clientY + 16}px`;
    };
    moveHud(e);

    let nextH = startH;
    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      nextH = Math.max(180, Math.round((startH + dy) / 16) * 16); // snap 16px
      section.style.minHeight = `${nextH}px`;
      hud.textContent = `${nextH}px · ${Math.round(nextH / 60)} rows`;
      moveHud(ev);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.removeAttribute('data-wb-grid-dragging');
      hud.remove();
      // Persist
      if (blockId && onUpdateBlockContent) {
        const block = blocksRef.current?.find(b => b.id === blockId);
        const c = block?.content || {};
        onUpdateBlockContent(blockId, { ...c, _fluid_section_height: nextH });
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Public helpers consumed by useFluidTextDrag for snap guides ──
  return {
    /** Exposed so <FluidOverlayLayer>'s portal-rendered button can wire it. */
    startResize,
    /**
     * Toggle the dragging state on the body — drives the brighter grid
     * + locks the grid visibility while a drag is in progress.
     */
    setDragging: (on) => {
      if (on) document.body.setAttribute('data-wb-grid-dragging', '1');
      else    document.body.removeAttribute('data-wb-grid-dragging');
    },
    /**
     * Render fixed-position blue snap-guide lines on the document body.
     * `guides` = [{ kind: 'v'|'h', position: <px>, meta?: <string> }, ...].
     * Call with `[]` to clear.
     * Iter 198e — meta chip: when a guide includes `.meta`, render a small
     * cyan pill at the guide origin so the vendor sees WHY it snapped
     * ("col 12/24", "thirds", "center", etc.).
     */
    renderSnapGuides: (guides) => {
      // Wipe previous
      document.querySelectorAll('.wb-snap-guide-v, .wb-snap-guide-h, .wb-snap-badge').forEach(n => n.remove());
      if (!guides || guides.length === 0) return;
      guides.forEach(g => {
        const el = document.createElement('div');
        el.className = g.kind === 'v' ? 'wb-snap-guide-v' : 'wb-snap-guide-h';
        if (g.kind === 'v') el.style.left = `${g.position}px`;
        else                el.style.top  = `${g.position}px`;
        document.body.appendChild(el);
        if (g.meta) {
          const badge = document.createElement('div');
          badge.className = 'wb-snap-badge';
          badge.textContent = g.meta;
          if (g.kind === 'v') {
            badge.style.left = `${g.position + 8}px`;
            badge.style.top = '12px';
          } else {
            badge.style.top = `${g.position + 6}px`;
            badge.style.left = '50%';
            badge.style.transform = 'translateX(-50%)';
          }
          document.body.appendChild(badge);
        }
      });
    },
  };
}
